/**
 * Kai query parser — deterministic, local, free.
 *
 * Turns "show overdue Nautilus tasks assigned to Franky" into a KaiQuery with
 * no network call. It is a span-tagging scanner, not a grammar: it walks the
 * sentence matching the longest phrase it recognises (from the synonym tables
 * PLUS the user's real tag and project names), consumes that span, and moves on.
 *
 * What it cannot confidently read, it hands to the LLM — see shouldEscalate().
 * Escalating is cheap; guessing is not. Kai is rate-limited to 5 requests/minute,
 * so keeping the common phrasings local is what makes the feature usable.
 *
 * Pure: no DOM, no globals, no clock (pass `now`).
 *
 * @module kaiQueryParser
 */

import {
    STATUS_GROUP_ALIASES, STATUS_ALIASES, PRIORITY_ALIASES, LINK_TYPE_ALIASES,
    DATE_PRESET_ALIASES, DATE_FIELD_CUES, PROJECT_STATUS_ALIASES, PROJECT_STATUS_GROUP_ALIASES,
    PROJECT_TASK_FILTER_ALIASES, UPDATED_FILTER_ALIASES, ENTITY_ALIASES, SCOPE_ALIASES,
    VIEW_ALIASES, NONE_TAG_PHRASES, NONE_PROJECT_PHRASES, NEGATION_TOKENS, TAG_AND_TOKENS,
    TAG_PREPOSITIONS, PROJECT_PREPOSITIONS, SEARCH_PREPOSITIONS, MODE_TOKENS,
    UNSUPPORTED_TERMS, LIMIT_PATTERN, SORT_PATTERN, GROUP_PATTERN, STOPWORDS,
    TAG_CUES, PROJECT_CUES, COMMON_WORDS, QUERY_INTENT_PATTERN, CREATE_INTENT_PATTERN,
    UPDATED_WINDOW_PATTERN, UPDATED_WINDOW_EXACT, COMPLETION_PATTERN, COMPLETION_PHRASES,
    ACTIVITY_VERBS, DUPLICATE_TITLE_PHRASES, SPANISH_NUMBER_WORDS,
    RECENCY_UPDATED_CUES, RECENCY_CREATED_CUES, RECENCY_GENERIC_CUES
} from '../config/kaiFilterVocabulary.js';

import {
    createEmptyKaiQuery, nearestNames, levenshtein, countKaiQueryFilters, KAI_NONE,
    validateKaiQueryAgainstVocabulary,
    projectNameAliases, PROJECT_NAME_PREFIX_WORDS
} from '../../utils/kai-query-shared.js';
import { isoDateInTimeZone } from './date.js';

/** Confidence below this escalates to the LLM. */
export const KAI_CONFIDENCE_THRESHOLD = 0.7;

/**
 * Structural words that are never a tag or project name, even if the user's data
 * contains one by that name and even if a cue precedes it. They are the connective
 * tissue of the sentence ("without tags AND without a date"), not filter values.
 */
const STRUCTURAL_WORDS = new Set([
    'and', 'or', 'not', 'without', 'with', 'no', 'the', 'a', 'an', 'of', 'to', 'in',
    'on', 'for', 'that', 'have', 'has', 'do', 'dont', 'any', 'all',
    'y', 'o', 'sin', 'con', 'no', 'de', 'la', 'el', 'los', 'las', 'un', 'una', 'que'
]);

/**
 * Should Auto-detect treat this as a query rather than a create request?
 *
 * "Show me…", "Find…", "Which tasks…" are questions about existing work. Without
 * this, Auto-detect falls through to the create pipeline and cheerfully offers to
 * make new tasks out of a request to LIST them.
 *
 * With the user's vocabulary, a verbless phrase also counts when it is nothing but
 * filters over a real name or the plural entity ("tareas de Campaña 3").
 */
export function isKaiQueryLikeInstruction(text, vocabulary = null, { activePage = 'tasks' } = {}) {
    const value = normalizeText(text);
    if (!value) return false;
    if (CREATE_INTENT_PATTERN.test(value)) return false;
    if (QUERY_INTENT_PATTERN.test(value)) return true;
    return !!vocabulary && isVerblessFilterPhrase(value, vocabulary, activePage);
}

// "una tarea…", "nueva tarea…", "Tarea para…": asking for ONE new item.
const NEW_ITEM_PATTERN = /\b(?:una?|a|an|nuev[oa]s?|new|otr[oa]|another|\d+|dos|tres|cuatro|cinco|two|three|four|five)\s+(?:sub)?(?:tareas?|tasks?|proyectos?|projects?)\b/;
const SINGULAR_ITEM_START = /^(?:sub)?(?:tarea|task)\b/;
// Connectors a filter phrase may leave behind ("tareas EN Campaña 3", "qué tareas TIENE…").
const FILTER_PHRASE_GLUE = new Set([
    'en', 'para', 'con', 'de', 'del', 'el', 'la', 'las', 'los', 'que', 'mis', 'sus', 'todas', 'todos',
    'y', 'o', 'tiene', 'tienen', 'hay', 'estan', 'esta', 'son', 'mine', 'there'
]);

/**
 * No verb, but every word is a filter, and it names the plural entity ("tareas",
 * "projects") or a real project/tag ("Campaña 3"). A task title ("Revisión urgente",
 * "Revisar muestras de campaña 3") fails one of those tests and stays a create request.
 */
function isVerblessFilterPhrase(value, vocabulary, activePage) {
    if (NEW_ITEM_PATTERN.test(value) || SINGULAR_ITEM_START.test(value)) return false;
    // Parse exactly as Find will, so Auto never routes to a Find that filters nothing.
    const result = parseKaiQuery(value, vocabulary, { activePage });
    const { query } = result;
    // "objetivo 1" shared by two real projects is still a question — Find asks which.
    const asksWhich = query.unresolved.length > 0
        && query.unresolved.every(note => note.kind === 'ambiguous' && (note.candidates?.length > 1 || note.kinds?.length > 0));
    if (result.escalate || query.unsupported.length || (query.unresolved.length && !asksWhich)) return false;
    if (result.leftovers.some(token => !FILTER_PHRASE_GLUE.has(token))) return false;
    // Count what survives validation — the Projects list drops a project filter.
    if (countKaiQueryFilters(validateKaiQueryAgainstVocabulary(query, vocabulary).query) === 0 && !asksWhich) return false;
    const namesEntity = /\b(?:tareas|tasks|proyectos|projects)\b/.test(value);
    const liveNames = new Set([
        ...(vocabulary.projects || []).map(project => project?.name),
        ...(vocabulary.tags || []).map(tag => (typeof tag === 'string' ? tag : tag?.name))
    ]);
    const asksWhichName = query.unresolved.some(note => note.kinds?.length
        || (note.candidates?.length > 1 && note.candidates.every(name => liveNames.has(name))));
    const namesLive = asksWhichName || result.matched.some(hit => hit.dimension === 'project' || hit.dimension === 'tag');
    return namesEntity || namesLive;
}

const MAX_NGRAM = 4;
const RANGE_MARKERS = { before: 'to', until: 'to', by: 'to', after: 'from', since: 'from', from: 'from', antes: 'to', despues: 'from', desde: 'from' };
const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const WEEKDAYS_ES = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'];
const MONTHS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
const MONTHS_ES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

export function normalizeText(value) {
    return String(value || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/\b(\d{1,2})[/.](\d{1,2})(?:[/.](\d{4}))?\b/g, (_, day, month, year) => `${day}-${month}${year ? `-${year}` : ''}`)
        .replace(/[^\w\s-]/g, ' ')
        .replace(/_/g, ' ')
        // A hyphen becomes a space so "highest-priority" tokenizes like the
        // "highest priority" a user would otherwise type — but NOT between digits,
        // which would shred an ISO date (2026-07-20) into three numbers.
        // Written with a callback rather than a lookbehind: iOS Safari only gained
        // lookbehind in 16.4, and this file ships to phones.
        .replace(/-+/g, (match, offset, whole) => {
            const before = whole[offset - 1] || '';
            const after = whole[offset + match.length] || '';
            return (/\d/.test(before) && /\d/.test(after)) ? match : ' ';
        })
        .replace(/\s+/g, ' ')
        .trim();
}

/**
 * @param {string} text - the user's request
 * @param {Object} vocabulary - from buildKaiVocabulary()
 * @param {Object} options
 * @param {Date}   options.now
 * @param {Object} options.previousQuery - for follow-ups
 * @param {string} options.activePage - 'tasks' | 'projects' | ... (entity default)
 * @returns {{query, confidence, escalate, matched, leftovers}}
 */
export function parseKaiQuery(text, vocabulary = {}, options = {}) {
    const { now: instant = new Date(), previousQuery = null, activePage = 'tasks' } = options;
    // Use the user's calendar even when their configured zone differs from the browser.
    const now = new Date(`${isoDateInTimeZone(instant, vocabulary.timezone)}T12:00:00`);
    const normalized = normalizeText(text);
    const tokens = normalized ? normalized.split(' ') : [];

    if (!tokens.length) {
        const empty = createEmptyKaiQuery('tasks');
        return { query: empty, confidence: 0, escalate: false, matched: [], leftovers: [], empty: true };
    }

    const mode = detectMode(normalized, previousQuery);

    // "clear all filters" / "start again" — no dictionary work needed, and it must
    // never cost an API call.
    if (mode === 'clear') {
        const query = createEmptyKaiQuery(resolveEntity(tokens, previousQuery, activePage, vocabulary));
        query.mode = 'clear';
        return { query, confidence: 1, escalate: false, matched: ['clear'], leftovers: [] };
    }

    const entity = resolveEntity(tokens, previousQuery, activePage, vocabulary);
    const query = createEmptyKaiQuery(entity);
    query.mode = mode;

    const consumed = new Array(tokens.length).fill(false);
    const matched = [];
    const unsupported = [];
    const unresolved = [];

    // 0a. "tasks with the same title" / "duplicate tasks" — a real filter, claimed
    //     first so a phrase that includes the entity noun ("duplicate TASKS",
    //     "tareas duplicadas") still matches before that noun is consumed below, and
    //     before the unsupported/free-text scans can mistake "duplicate" for either.
    if (entity !== 'projects') {
        for (const phrase of [...DUPLICATE_TITLE_PHRASES].sort((a, b) => b.length - a.length)) {
            const span = findSpan(tokens, consumed, normalizeText(phrase));
            if (!span) continue;
            consume(consumed, span);
            query.filters.duplicateTitles = true;
            matched.push({ span, dimension: 'duplicateTitles', phrase });
            break;
        }
    }

    // The entity noun is already captured by resolveEntity(); consume it so it
    // doesn't linger as an unresolved "name".
    for (let i = 0; i < tokens.length; i++) {
        if (ENTITY_ALIASES[tokens[i]]) consumed[i] = true;
    }

    // 0. "no assignee" / "unassigned" / "no project" BEFORE the unsupported scan.
    //    These ARE expressible — matchesTags()/matchesProject() understand a "none"
    //    sentinel — and they must not be swallowed by the bare "assignee" rule that
    //    exists to reject "group by assignee".
    for (const phrase of [...NONE_TAG_PHRASES].sort((a, b) => b.length - a.length)) {
        const span = findSpan(tokens, consumed, normalizeText(phrase));
        if (!span) continue;
        consume(consumed, span);
        if (!query.filters.tags.includes(KAI_NONE)) query.filters.tags.push(KAI_NONE);
        matched.push({ span, dimension: 'noneTag', phrase });
    }
    if (entity !== 'projects') {
        for (const phrase of [...NONE_PROJECT_PHRASES].sort((a, b) => b.length - a.length)) {
            const span = findSpan(tokens, consumed, normalizeText(phrase));
            if (!span) continue;
            consume(consumed, span);
            if (!query.filters.projects.includes(KAI_NONE)) query.filters.projects.push(KAI_NONE);
            matched.push({ span, dimension: 'noneProject', phrase });
        }
    }

    // 1. Unsupported dimensions — "sort by due date" must be recognised as a thing
    //    we cannot do, not silently dropped as noise.
    for (const { phrases, reason } of UNSUPPORTED_TERMS) {
        for (const phrase of phrases) {
            const span = findSpan(tokens, consumed, normalizeText(phrase));
            if (span) {
                consume(consumed, span);
                unsupported.push({ term: phrase, reason });
                matched.push({ span, dimension: 'unsupported', value: reason });
                break;
            }
        }
    }
    // Parse numerical limit (e.g., "5 most recently updated", "las últimas 3 tareas")
    const limitHit = normalized.match(LIMIT_PATTERN);
    if (limitHit) {
        const limitText = limitHit[0].trim();
        // Extract the number — digit, English word, or Spanish word.
        const numMatch = limitText.match(/\b(\d+|one|two|three|four|five|six|seven|eight|nine|ten|uno|una|dos|tres|cuatro|cinco|seis|siete|ocho|nueve|diez)\b/i);
        if (numMatch) {
            const wordToNum = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10, ...SPANISH_NUMBER_WORDS };
            const token = numMatch[1].toLowerCase();
            const num = wordToNum[token] || parseInt(token, 10);
            if (!isNaN(num) && num > 0) {
                query.filters.limit = num;
                matched.push({ dimension: 'limit', phrase: limitText });
                consumeMatchedWords(tokens, consumed, limitText);
            }
        }
    }

    // Recency ranking ("the 5 most recently modified", "las últimas tareas creadas").
    // Only meaningful alongside a limit or an explicit recency cue; pairs with the
    // preview's newest-first slice. A verb-less "latest/últimas" defaults to updated.
    if (!query.filters.sort) {
        const hasCue = (cues) => cues.some(c => normalized.includes(normalizeText(c)));
        if (hasCue(RECENCY_CREATED_CUES)) {
            query.filters.sort = 'recent-created';
        } else if (hasCue(RECENCY_UPDATED_CUES)) {
            query.filters.sort = 'recent-updated';
        } else if (query.filters.limit && hasCue(RECENCY_GENERIC_CUES)) {
            query.filters.sort = 'recent-updated';
        }
        if (query.filters.sort) matched.push({ dimension: 'sort', value: query.filters.sort });
    }

    // Arbitrary column sort / group are unsupported (recency handled just above).
    const patterns = [[SORT_PATTERN, 'sort'], [GROUP_PATTERN, 'groupBy']];
    for (const [pattern, reason] of patterns) {
        const hit = normalized.match(pattern);
        if (hit && !unsupported.some(u => u.reason === reason)) {
            unsupported.push({ term: hit[0].trim(), reason });
            consumeMatchedWords(tokens, consumed, hit[0]);
            matched.push({ dimension: 'unsupported', value: reason });
        }
    }

    // 1b. Activity windows: "created last week", "modified in March", "completed
    //     this month". Runs before everything date-related so the period is claimed
    //     for the timestamp the verb names.
    applyActivityRanges(tokens, consumed, query, now, matched);

    // 1c. The recency shortcut ("recently updated" → 24h) and sub-day windows, only
    //     when 1b did not already turn it into a proper updated range.
    if (!query.filters.updatedFrom && !query.filters.updatedTo) {
        applyUpdatedWindow(text, tokens, consumed, query, matched, unsupported);
    }

    // 1d. Project completion ("more than 75% of their tasks done").
    if (entity === 'projects') {
        applyCompletionFilter(text, tokens, consumed, query, matched);
    }

    // 2. Explicit date ranges ("before Friday", "between July 15 and July 31").
    //    Runs before the dictionary so "before Friday" isn't half-eaten.
    applyDateRanges(tokens, consumed, query, now, matched);

    // Exact deadlines must claim their words before "con fecha" or a month
    // name can be read as a generic preset or an assignee.
    applyExactDeadline(tokens, consumed, query, now, matched, unresolved);

    // 2b. A bare month name ("due in March of this year") is a whole-month range.
    applyMonthRange(tokens, consumed, query, now, matched, vocabulary, entity);

    // 2c. Whole-year ("in 2025", "due this year") and relative-week ("next week",
    //     "last week") ranges — the periods a bare preset cannot express.
    applyPeriodRange(tokens, consumed, query, now, matched);

    // 3. The main longest-match scan over aliases + the user's real names.
    const dictionary = buildDictionary(entity, vocabulary);
    scanDictionary(tokens, consumed, dictionary, query, matched, unsupported, unresolved);

    // 4. Free-text search ("containing shipment", "mentioning Grenoble").
    applySearchPhrase(tokens, consumed, query, matched);

    // 5. Part of a project name after a cue ("de campaña 3", "del objetivo campaña").
    resolveProjectFragments(tokens, consumed, query, vocabulary, entity, unresolved, matched);

    // 6. Whatever is left after a resolving preposition is a name we don't know.
    resolveLeftovers(tokens, consumed, query, vocabulary, entity, unresolved, matched);

    query.unsupported = unsupported;
    query.unresolved = unresolved;

    const leftovers = tokens.filter((token, i) => !consumed[i] && !STOPWORDS.has(token));
    const confidence = scoreConfidence({ tokens, consumed, unresolved, matched, leftovers });

    return {
        query,
        confidence,
        escalate: shouldEscalate({ query, confidence, unresolved, leftovers, normalized, matched, unsupported }),
        matched,
        leftovers
    };
}

// ---------------------------------------------------------------------------
// Entity / mode
// ---------------------------------------------------------------------------

function resolveEntity(tokens, previousQuery, activePage, vocabulary = {}) {
    for (let i = 0; i < tokens.length; i++) {
        const hit = ENTITY_ALIASES[tokens[i]];
        // "proyecto Campaña 3" points at one project's tasks; it is not a request to
        // list projects. Only the singular noun followed by a real project name.
        if (hit === 'projects' && (tokens[i] === 'proyecto' || tokens[i] === 'project')
            && startsWithProjectName(tokens.slice(i + 1), vocabulary)) continue;
        if (hit) return hit;
    }
    if (previousQuery?.entity) return previousQuery.entity;
    return activePage === 'projects' ? 'projects' : 'tasks';
}

function startsWithProjectName(tokens, vocabulary) {
    const rest = ` ${tokens.join(' ')} `;
    return (vocabulary.projects || []).some(project => projectNameAliases(project?.name)
        .some(alias => {
            const key = normalizeText(alias);
            return key && rest.startsWith(` ${key} `);
        }));
}

function detectMode(normalized, previousQuery) {
    // A follow-up only makes sense when there is something to follow up on.
    const sorted = Object.keys(MODE_TOKENS).sort((a, b) => b.length - a.length);
    for (const phrase of sorted) {
        const key = normalizeText(phrase);
        const re = new RegExp(`(^|\\s)${escapeRegex(key)}(\\s|$)`);
        if (!re.test(normalized)) continue;
        const mode = MODE_TOKENS[phrase];
        if (mode === 'clear') return 'clear';
        if (previousQuery) return mode;
    }
    return 'replace';
}

function escapeRegex(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// ---------------------------------------------------------------------------
// Dictionary
// ---------------------------------------------------------------------------

/**
 * One flat phrase → meaning map, built per entity.
 *
 * Entity matters: "active" means "not done" for a task but is a real derived
 * status for a project, and "completed" is a status group for tasks but a
 * status value for projects. Building the dictionary after resolving the entity
 * removes the ambiguity instead of trying to guess it later.
 */
function buildDictionary(entity, vocabulary) {
    const dict = new Map();
    const put = (phrase, meaning) => {
        const key = normalizeText(phrase);
        // First writer wins: alias tables are registered before live names, so a
        // project literally called "Done" cannot hijack the `done` status.
        if (key && !dict.has(key)) dict.set(key, meaning);
    };

    // Registered before everything else so multi-word update phrases ("updated
    // this week") win over the bare date preset ("this week").
    for (const [phrase, value] of Object.entries(UPDATED_FILTER_ALIASES)) put(phrase, { dimension: 'updatedFilter', value });

    if (entity === 'projects') {
        for (const [phrase, value] of Object.entries(PROJECT_STATUS_GROUP_ALIASES)) put(phrase, { dimension: 'projectStatusGroup', value });
        for (const [phrase, value] of Object.entries(PROJECT_STATUS_ALIASES)) put(phrase, { dimension: 'projectStatus', value });
        for (const [phrase, value] of Object.entries(PROJECT_TASK_FILTER_ALIASES)) put(phrase, { dimension: 'projectTaskFilter', value });

        // projectFilterState can only ask "does this project have tasks at all?".
        // "projects with blocked tasks" / "projects with overdue tasks" would need
        // an aggregate over each project's tasks, which no filter expresses — so
        // recognise the intent and refuse it rather than dropping it silently.
        for (const phrase of Object.keys(LINK_TYPE_ALIASES)) put(phrase, { dimension: 'unsupportedProjectAggregate' });
        for (const phrase of Object.keys(DATE_PRESET_ALIASES)) put(phrase, { dimension: 'unsupportedProjectAggregate' });
        for (const phrase of Object.keys(PRIORITY_ALIASES)) put(phrase, { dimension: 'unsupportedProjectAggregate' });
    } else {
        for (const [phrase, value] of Object.entries(STATUS_GROUP_ALIASES)) put(phrase, { dimension: 'statusGroup', value });
        for (const [phrase, value] of Object.entries(STATUS_ALIASES)) put(phrase, { dimension: 'status', value });
        for (const [phrase, value] of Object.entries(PRIORITY_ALIASES)) put(phrase, { dimension: 'priority', value });
        for (const [phrase, value] of Object.entries(LINK_TYPE_ALIASES)) put(phrase, { dimension: 'linkType', value });
        for (const [phrase, value] of Object.entries(DATE_PRESET_ALIASES)) put(phrase, { dimension: 'datePreset', value });
        for (const phrase of NONE_PROJECT_PHRASES) put(phrase, { dimension: 'noneProject' });
    }

    for (const phrase of NONE_TAG_PHRASES) put(phrase, { dimension: 'noneTag' });
    for (const [phrase, value] of Object.entries(SCOPE_ALIASES)) put(phrase, { dimension: 'scope', value });
    for (const [phrase, value] of Object.entries(VIEW_ALIASES)) put(phrase, { dimension: 'view', value });

    // Live data last, so it can never shadow a filter keyword.
    //
    // A name is registered under a `live` entry that can hold BOTH a tag and a
    // project reading — "Palythoa Study" is commonly both — leaving the choice to
    // the cue in the sentence rather than to whichever we happened to index first.
    //
    // `needsCue` marks names too ordinary to claim a bare word ("find", "tag",
    // "test"). Those only match when the user points at them explicitly.
    const putLive = (name, kind, value, { alias = false, label = name } = {}) => {
        const key = normalizeText(name);
        if (!key || key.length < 2) return;
        // A tag literally named "and"/"or"/"not" is a conjunction far more often than
        // a filter. These are never matched from natural language (still reachable via
        // the dropdown) — otherwise "without tags AND without a date" reads "and" as a
        // tag. This must win even when a cue word precedes it.
        if (STRUCTURAL_WORDS.has(key)) return;
        // Never let live data shadow a filter keyword ("done", "urgent", "overdue").
        const existing = dict.get(key);
        if (existing && existing.dimension !== 'live') return;

        const entry = existing || {
            dimension: 'live',
            // A one-word alias ("Obj. 4: Muestreo" → "muestreo") is derived, not
            // typed by the user as a name, so it only claims a bare word when cued.
            needsCue: !key.includes(' ') && (alias || STOPWORDS.has(key) || COMMON_WORDS.has(key))
        };
        if (kind === 'project') {
            if (entry.project && entry.project !== value) {
                // A project's real name beats another project's derived alias; two
                // aliases that collide are an ambiguity the user settles.
                if (entry.projectExact && alias) return;
                entry.projectCandidates.push(label);
                return;
            }
            if (!entry.project) {
                entry.projectExact = !alias;
                entry.projectCandidates = [label];
            }
        }
        entry[kind] = value;
        dict.set(key, entry);
    };

    const tagSource = entity === 'projects' && (vocabulary.projectTags || []).length
        ? vocabulary.projectTags
        : vocabulary.tags;
    for (const tag of (tagSource || [])) {
        const name = typeof tag === 'string' ? tag : tag?.name;
        if (name) putLive(name, 'tag', String(name));
    }
    // Real names first, so a project literally called "Campaña 3" outranks the
    // "Campaña 3" alias of "Obj. 1: CAMPAÑA 3".
    const projects = (vocabulary.projects || []).filter(project => project?.name);
    for (const project of projects) putLive(project.name, 'project', String(project.id));
    for (const project of projects) {
        for (const alias of projectNameAliases(project.name).slice(1)) {
            putLive(alias, 'project', String(project.id), { alias: true, label: project.name });
        }
    }

    return dict;
}

/**
 * Which kind of thing did the user say this name was?
 * Looks back a few tokens for an explicit "tag" / "project" cue.
 */
function detectKindCue(tokens, start) {
    const window = tokens.slice(Math.max(0, start - 3), start);
    for (let i = window.length - 1; i >= 0; i--) {
        const token = window[i];
        if (TAG_CUES.includes(token)) return 'tag';
        if (PROJECT_CUES.includes(token)) return 'project';
    }
    return '';
}

function scanDictionary(tokens, consumed, dictionary, query, matched, unsupported, unresolved) {
    let i = 0;
    while (i < tokens.length) {
        if (consumed[i]) { i++; continue; }
        let hit = null;
        for (let n = Math.min(MAX_NGRAM, tokens.length - i); n >= 1; n--) {
            const span = { start: i, end: i + n };
            if (spanIsConsumed(consumed, span)) continue;
            const phrase = tokens.slice(i, i + n).join(' ');
            const meaning = dictionary.get(phrase);
            if (meaning) { hit = { span, meaning, phrase }; break; }
        }
        if (!hit) { i++; continue; }

        if (hit.meaning.dimension === 'unsupportedProjectAggregate') {
            if (!unsupported.some(u => u.reason === 'projectAggregate')) {
                unsupported.push({ term: hit.phrase, reason: 'projectAggregate' });
            }
            consume(consumed, hit.span);
            matched.push({ span: hit.span, phrase: hit.phrase, dimension: 'unsupported' });
            i = hit.span.end;
            continue;
        }

        // A live tag/project name. The cue in the sentence decides what it is.
        if (hit.meaning.dimension === 'live') {
            const cue = detectKindCue(tokens, hit.span.start);
            const entry = hit.meaning;

            // Ordinary words ("find", "tag", "test") only count when pointed at.
            if (entry.needsCue && !cue) { i = hit.span.end; continue; }

            const askWhichKind = (kinds) => {
                unresolved.push({ term: hit.phrase, kind: 'ambiguous', candidates: [hit.phrase], kinds });
                consume(consumed, hit.span);
                matched.push({ span: hit.span, phrase: hit.phrase, dimension: 'unresolved' });
            };

            let kind = cue;
            if (kind === 'tag' && !entry.tag) {
                // "with tag Palythoa Study" when that name is only a PROJECT. Offer the
                // project rather than quietly filtering by something they didn't ask for.
                if (entry.project) {
                    askWhichKind(['project']);
                    i = hit.span.end;
                    continue;
                }
                kind = '';
            }
            if (kind === 'project' && !entry.project) kind = entry.tag ? 'tag' : '';
            if (!kind) kind = entry.tag ? 'tag' : 'project';

            // Genuinely both, with nothing to break the tie — ask, never guess.
            if (!cue && entry.tag && entry.project) {
                askWhichKind(['tag', 'project']);
                i = hit.span.end;
                continue;
            }

            if (kind === 'project' && entry.projectCandidates?.length > 1) {
                unresolved.push({ term: hit.phrase, kind: 'ambiguous', candidates: [...entry.projectCandidates].sort() });
                consume(consumed, hit.span);
                matched.push({ span: hit.span, phrase: hit.phrase, dimension: 'unresolved' });
                i = hit.span.end;
                continue;
            }

            const negatedLive = hasNegationBefore(tokens, consumed, hit.span.start);
            applyMeaning(query, {
                dimension: kind,
                value: kind === 'tag' ? entry.tag : entry.project
            }, { negated: negatedLive, tokens, span: hit.span });
            consume(consumed, hit.span);
            if (negatedLive) consumed[negatedLive.index] = true;
            matched.push({ span: hit.span, phrase: hit.phrase, dimension: kind });
            i = hit.span.end;
            continue;
        }

        const negated = hasNegationBefore(tokens, consumed, hit.span.start);
        applyMeaning(query, hit.meaning, { negated, tokens, span: hit.span });
        consume(consumed, hit.span);
        if (negated) consumed[negated.index] = true;
        matched.push({ span: hit.span, phrase: hit.phrase, dimension: hit.meaning.dimension, negated: !!negated });
        i = hit.span.end;
    }
}

/**
 * A negation up to 3 tokens back flips the dimension to exclude mode:
 * "tasks that do not have the backend tag", "except low-priority".
 */
function hasNegationBefore(tokens, consumed, start) {
    for (let i = start - 1; i >= Math.max(0, start - 3); i--) {
        if (consumed[i]) break;
        if (NEGATION_TOKENS.includes(tokens[i])) return { index: i, token: tokens[i] };
        // Don't reach across a filler word chain longer than the window.
        if (!STOPWORDS.has(tokens[i]) && !['do', 'does', 'have', 'the', 'a', 'an', 'is', 'are'].includes(tokens[i])) break;
    }
    return null;
}

function applyMeaning(query, meaning, { negated, tokens, span }) {
    const f = query.filters;
    switch (meaning.dimension) {
        case 'statusGroup': {
            // "open" already means "exclude done"; negating it means the opposite.
            const exclude = negated ? !meaning.value.excludeMode : meaning.value.excludeMode;
            for (const status of meaning.value.statuses) if (!f.statuses.includes(status)) f.statuses.push(status);
            f.statusExcludeMode = exclude;
            break;
        }
        case 'status':
            if (!f.statuses.includes(meaning.value)) f.statuses.push(meaning.value);
            if (negated) f.statusExcludeMode = true;
            break;
        case 'priority':
            if (!f.priorities.includes(meaning.value)) f.priorities.push(meaning.value);
            if (negated) f.priorityExcludeMode = true;
            break;
        case 'linkType':
            if (!f.linkTypes.includes(meaning.value)) f.linkTypes.push(meaning.value);
            break;
        case 'datePreset':
            if (!f.datePresets.includes(meaning.value)) f.datePresets.push(meaning.value);
            break;
        case 'project':
            if (!f.projects.includes(meaning.value)) f.projects.push(meaning.value);
            if (negated) f.projectExcludeMode = true;
            break;
        case 'noneProject':
            if (!f.projects.includes(KAI_NONE)) f.projects.push(KAI_NONE);
            break;
        case 'tag':
            if (!f.tags.includes(meaning.value)) f.tags.push(meaning.value);
            if (negated) f.tagExcludeMode = true;
            if (hasTagAndBefore(tokens, span.start)) f.tagAndMode = true;
            break;
        case 'noneTag':
            if (!f.tags.includes(KAI_NONE)) f.tags.push(KAI_NONE);
            break;
        case 'projectStatusGroup': {
            const exclude = negated ? !meaning.value.excludeMode : meaning.value.excludeMode;
            for (const status of meaning.value.statuses) if (!f.projectStatuses.includes(status)) f.projectStatuses.push(status);
            f.projectStatusExcludeMode = exclude;
            break;
        }
        case 'projectStatus':
            if (!f.projectStatuses.includes(meaning.value)) f.projectStatuses.push(meaning.value);
            if (negated) f.projectStatusExcludeMode = true;
            break;
        case 'projectTaskFilter':
            f.taskFilter = meaning.value;
            break;
        case 'updatedFilter':
            f.updatedFilter = meaning.value;
            break;
        case 'scope':
            query.scope = meaning.value;
            break;
        case 'view':
            query.view = meaning.value;
            break;
        default:
            break;
    }
}

/** "tagged both frontend and urgent" → the task must carry every tag. */
function hasTagAndBefore(tokens, start) {
    const window = tokens.slice(Math.max(0, start - 5), start).join(' ');
    return TAG_AND_TOKENS.some(token => window.includes(normalizeText(token)));
}

// ---------------------------------------------------------------------------
// Update windows and completion — both read the RAW text, because normalizeText()
// strips the "%" sign and the digits alone are meaningless.
// ---------------------------------------------------------------------------

/**
 * "in the past 4 days". Nautilus's updated filter has fixed windows (5m, 30m, 24h,
 * week, month) and no arbitrary N. Rounding 4 days up to "this week" would hand back
 * 7 days of tasks, so only exact matches are accepted; the rest is reported honestly.
 */
function applyUpdatedWindow(text, tokens, consumed, query, matched, unsupported) {
    // Match on the tokens still available — a period already claimed by an activity
    // verb ("modified in the past 4 days") must not be re-read here as an unsupported
    // fixed window.
    const remaining = tokens.map((token, i) => (consumed[i] ? ' ' : token)).join(' ');
    const hit = remaining.match(UPDATED_WINDOW_PATTERN);
    if (!hit) return;

    const amount = Number.parseInt(hit[1], 10);
    const unit = String(hit[2] || '').toLowerCase().replace(/s$/, '').replace(/^mins?$/, 'minute').replace(/^hrs?$/, 'hour');
    const exact = UPDATED_WINDOW_EXACT[`${unit}:${amount}`];

    if (exact) {
        query.filters.updatedFilter = exact;
        matched.push({ dimension: 'updatedFilter', phrase: hit[0] });
    } else if (!unsupported.some(note => note.reason === 'updatedWindow')) {
        unsupported.push({ term: hit[0].trim(), reason: 'updatedWindow' });
        matched.push({ dimension: 'unsupported', phrase: hit[0] });
    }
    consumeMatchedWords(tokens, consumed, hit[0]);
}

/**
 * "more than 75% of their tasks done" → completionMin 76.
 * Comparators are read literally; see COMPLETION_PATTERN.
 */
function applyCompletionFilter(text, tokens, consumed, query, matched) {
    const raw = String(text || '').toLowerCase();
    const f = query.filters;

    for (const [phrase, bounds] of Object.entries(COMPLETION_PHRASES)) {
        if (!containsWholePhrase(raw, phrase)) continue;
        f.completionMin = bounds.min;
        f.completionMax = bounds.max;
        consumeMatchedWords(tokens, consumed, phrase);
        matched.push({ dimension: 'completion', phrase });
        return;
    }

    const hit = raw.match(COMPLETION_PATTERN);
    if (!hit) return;

    const comparator = hit[1].toLowerCase();
    const first = Number.parseInt(hit[2], 10);
    const second = hit[3] != null ? Number.parseInt(hit[3], 10) : null;
    if (!Number.isFinite(first) || first < 0 || first > 100) return;

    if (comparator === 'between' && second != null) {
        f.completionMin = Math.min(first, second);
        f.completionMax = Math.max(first, second);
    } else if (/^(more than|greater than|over|above)$/.test(comparator)) {
        f.completionMin = Math.min(first + 1, 100);
    } else if (/^(at least|no less than|minimum)/.test(comparator)) {
        f.completionMin = first;
    } else if (/^(less than|under|below|fewer than)$/.test(comparator)) {
        f.completionMax = Math.max(first - 1, 0);
    } else if (/^(at most|no more than|maximum)/.test(comparator)) {
        f.completionMax = first;
    } else if (/^(exactly|precisely)$/.test(comparator)) {
        f.completionMin = first;
        f.completionMax = first;
    }

    consumeMatchedWords(tokens, consumed, hit[0]);
    // "of their tasks done" is scaffolding around the number, not a filter.
    consumeMatchedWords(tokens, consumed, 'of their tasks done complete completed');
    matched.push({ dimension: 'completion', phrase: hit[0] });
}

/**
 * Substring matching is not good enough here: "at least 5|0% done|" contains the
 * literal "0% done", which would silently turn a "50% or more" request into a
 * "not started" one. The match must start and end on a boundary.
 */
function containsWholePhrase(haystack, phrase) {
    let from = 0;
    for (;;) {
        const index = haystack.indexOf(phrase, from);
        if (index === -1) return false;
        const before = index > 0 ? haystack[index - 1] : '';
        const after = haystack[index + phrase.length] || '';
        const isBoundary = (char) => char === '' || !/[a-z0-9]/i.test(char);
        if (isBoundary(before) && isBoundary(after)) return true;
        from = index + 1;
    }
}

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

/**
 * A named month is a date, not a tag. "due in March of this year" must become a
 * March 1–31 range — hunting for a tag called "march" and then returning all 149
 * tasks is the opposite of what the sentence says.
 *
 * Runs after the explicit before/after/between handling, so "after July 20" keeps
 * its exact-date meaning and only a *bare* month becomes a whole-month range.
 */
function applyMonthRange(tokens, consumed, query, now, matched, vocabulary, entity) {
    const f = query.filters;
    if (f.dateFrom || f.dateTo) return; // an explicit range already won

    for (let i = 0; i < tokens.length; i++) {
        if (consumed[i]) continue;
        const monthIndex = MONTHS.indexOf(tokens[i]) >= 0 ? MONTHS.indexOf(tokens[i]) : MONTHS_ES.indexOf(tokens[i]);
        if (monthIndex < 0) continue;
        // A person's tag may also be a month name (for example, "Julio"). An
        // explicit assignee/tag cue must select the live tag; genuine date wording
        // such as "due in July" still reaches the month handling below.
        if (isExplicitLiveTagReference(tokens, i, vocabulary, entity)) continue;

        let end = i + 1;
        let year = now.getFullYear();
        let day = null;

        // "March 2026" / "March 15"
        const next = Number.parseInt(tokens[i + 1], 10);
        if (Number.isFinite(next)) {
            if (next > 1900) { year = next; end = i + 2; }
            else if (next >= 1 && next <= 31) {
                day = next;
                end = i + 2;
                const after = Number.parseInt(tokens[i + 2], 10);
                if (Number.isFinite(after) && after > 1900) { year = after; end = i + 3; }
            }
        }

        // "of this year" / "last year" / "next year"
        const tail = tokens.slice(end, end + 3).join(' ');
        const yearPhrase = tail.match(/^(?:of\s+)?(this|last|next|el proximo|el pasado)\s+year|^(?:de\s+)?(este|el proximo|el pasado)\s+ano/);
        if (yearPhrase) {
            const word = yearPhrase[1] || yearPhrase[2] || '';
            if (/last|pasado/.test(word)) year -= 1;
            else if (/next|proximo/.test(word)) year += 1;
            end += yearPhrase[0].split(' ').length;
        }

        if (day != null) {
            const iso = buildISO(year, monthIndex, day);
            f.dateFrom = iso;
            f.dateTo = iso;
        } else {
            f.dateFrom = buildISO(year, monthIndex, 1);
            f.dateTo = buildISO(year, monthIndex, daysInMonth(year, monthIndex));
        }

        consumeRange(consumed, i, Math.min(end, tokens.length));
        matched.push({ span: { start: i, end }, dimension: 'dateRange' });
        return;
    }
}

function isExplicitLiveTagReference(tokens, index, vocabulary, entity) {
    const tagSource = entity === 'projects' && (vocabulary.projectTags || []).length
        ? vocabulary.projectTags
        : vocabulary.tags;
    const isLiveTag = (tagSource || []).some(tag => {
        const name = typeof tag === 'string' ? tag : tag?.name;
        return name && normalizeText(name) === tokens[index];
    });
    if (!isLiveTag) return false;

    const context = precedingPhrase(tokens, index);
    return TAG_PREPOSITIONS.some(phrase => context.endsWith(normalizeText(phrase)));
}

function daysInMonth(year, monthIndex) {
    return new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
}

// ---------------------------------------------------------------------------
// Activity windows — created / updated / completed in some period
// ---------------------------------------------------------------------------

/**
 * "created last week", "modified in March", "resolved this month".
 *
 * The period is routed to the timestamp the verb names. A completion verb
 * ("closed"/"finished") also reads as the `done` status, so an activity range is
 * only taken when a real period phrase follows — otherwise the word is left for
 * the status handler, and "closed tasks" still means status:done.
 */
function applyActivityRanges(tokens, consumed, query, now, matched) {
    let target = null;
    let verbIndices = [];
    outer:
    for (const [kind, verbs] of Object.entries(ACTIVITY_VERBS)) {
        for (let i = 0; i < tokens.length; i++) {
            if (!consumed[i] && verbs.includes(tokens[i])) {
                target = kind;
                verbIndices = [i];
                break outer;
            }
        }
    }
    if (!target) return;

    // Find the first period phrase anywhere in the sentence.
    let period = null;
    for (let i = 0; i < tokens.length && !period; i++) {
        if (consumed[i]) continue;
        period = matchPeriodAt(tokens, i, now);
    }
    if (!period) return; // no period → the verb was a status word, leave it alone

    const f = query.filters;
    if (target === 'created') { f.createdFrom = period.from; f.createdTo = period.to; }
    else if (target === 'updated') { f.updatedFrom = period.from; f.updatedTo = period.to; }
    else { f.completedFrom = period.from; f.completedTo = period.to; }

    for (const idx of verbIndices) consumed[idx] = true;
    consumeRange(consumed, period.start, period.end);
    matched.push({ span: { start: period.start, end: period.end }, dimension: `${target}Range` });
}

/**
 * Resolve a relative-period phrase starting at tokens[i] into an inclusive
 * [from, to] ISO range. Returns { from, to, start, end } or null.
 *
 * Covers: today, yesterday; this/last week; this/last month; this/last/next year;
 * a named month (+ optional year); a bare year; and "the last / past N days | weeks
 * | months". Everything here is a calendar period — a "week" is Monday–Sunday, not
 * the app's forward 7-day preset window.
 */
function matchPeriodAt(tokens, i, now) {
    const t0 = tokens[i];
    const t1 = tokens[i + 1];
    const t2 = tokens[i + 2];
    const make = (from, to, end) => ({ from: toISO(from), to: toISO(to), start: i, end });

    if (t0 === 'today' || t0 === 'hoy') return make(now, now, i + 1);
    if (t0 === 'yesterday' || t0 === 'ayer') { const y = addDays(now, -1); return make(y, y, i + 1); }

    // "this/last week", "past week", "semana pasada", "esta semana".
    if ((t0 === 'this' || t0 === 'last' || t0 === 'past') && t1 === 'week') {
        const base = addDays(now, t0 === 'this' ? 0 : -7);
        const monday = startOfISOWeek(base);
        return make(monday, addDays(monday, 6), i + 2);
    }
    if (t0 === 'esta' && t1 === 'semana') { const m = startOfISOWeek(now); return make(m, addDays(m, 6), i + 2); }
    if ((t0 === 'semana') && (t1 === 'pasada')) { const m = startOfISOWeek(addDays(now, -7)); return make(m, addDays(m, 6), i + 2); }

    // "this/last month", "mes pasado", "este mes".
    if ((t0 === 'this' || t0 === 'last' || t0 === 'past') && t1 === 'month') {
        return monthRange(now.getFullYear(), now.getMonth() + (t0 === 'this' ? 0 : -1), i + 2);
    }
    if (t0 === 'este' && t1 === 'mes') return monthRange(now.getFullYear(), now.getMonth(), i + 2);
    if (t0 === 'mes' && t1 === 'pasado') return monthRange(now.getFullYear(), now.getMonth() - 1, i + 2);

    // "this/last/next year", bare year.
    if ((t0 === 'this' || t0 === 'last' || t0 === 'next') && t1 === 'year') {
        const year = now.getFullYear() + (t0 === 'last' ? -1 : t0 === 'next' ? 1 : 0);
        return make(new Date(year, 0, 1), new Date(year, 11, 31), i + 2);
    }
    if (/^(?:19|20)\d\d$/.test(t0)) {
        const year = Number.parseInt(t0, 10);
        return make(new Date(year, 0, 1), new Date(year, 11, 31), i + 1);
    }

    // "the last / past N days | weeks | months" (and Spanish "ultimos N dias").
    if ((t0 === 'last' || t0 === 'past' || t0 === 'ultimos' || t0 === 'ultimas')) {
        const n = wordToNumber(t1);
        const unit = normalizeUnit(t2);
        if (n && unit) {
            if (unit === 'day') return make(addDays(now, -(n - 1)), now, i + 3);
            if (unit === 'week') return make(addDays(now, -(7 * n - 1)), now, i + 3);
            if (unit === 'month') { const from = new Date(now); from.setMonth(from.getMonth() - n); return make(from, now, i + 3); }
        }
    }

    // A named month, optionally with a year or "of this/last year".
    const monthIndex = MONTHS.indexOf(t0) >= 0 ? MONTHS.indexOf(t0) : MONTHS_ES.indexOf(t0);
    if (monthIndex >= 0) {
        let year = now.getFullYear();
        let end = i + 1;
        const yearNum = Number.parseInt(t1, 10);
        if (Number.isFinite(yearNum) && yearNum > 1900) { year = yearNum; end = i + 2; }
        return monthRange(year, monthIndex, end);
    }

    return null;

    function monthRange(year, monthIdx, end) {
        // Normalize a rolled-over month (December of "last month" from January).
        const d = new Date(year, monthIdx, 1);
        const y = d.getFullYear();
        const m = d.getMonth();
        return { from: buildISO(y, m, 1), to: buildISO(y, m, daysInMonth(y, m)), start: i, end };
    }
}

function normalizeUnit(token) {
    if (!token) return '';
    const t = token.replace(/s$/, '');
    if (t === 'day' || t === 'dia') return 'day';
    if (t === 'week' || t === 'semana') return 'week';
    if (t === 'month' || t === 'mes' || token === 'meses') return 'month';
    return '';
}

const NUMBER_WORDS = {
    one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
    uno: 1, dos: 2, tres: 3, cuatro: 4, cinco: 5, seis: 6, siete: 7, ocho: 8, nueve: 9, diez: 10
};

function wordToNumber(token) {
    if (!token) return 0;
    const digit = Number.parseInt(token, 10);
    if (Number.isFinite(digit) && digit > 0) return digit;
    return NUMBER_WORDS[token] || 0;
}

/**
 * Whole-year and relative-week ranges — the periods a fixed preset cannot express.
 *
 * "this year" is Jan 1 – Dec 31. "next week" and "last week" are Monday–Sunday of
 * the adjacent week (ISO weeks, Monday-start), which is what people mean by "week"
 * even though the app's own presets count forward in fixed 7-day windows.
 */
function applyPeriodRange(tokens, consumed, query, now, matched) {
    const f = query.filters;
    if (f.dateFrom || f.dateTo) return; // an explicit range or a month already won

    for (let i = 0; i < tokens.length; i++) {
        if (consumed[i]) continue;

        // Bare 4-digit year: "in 2025", "due 2026".
        if (/^(?:19|20)\d\d$/.test(tokens[i])) {
            const year = Number.parseInt(tokens[i], 10);
            f.dateFrom = buildISO(year, 0, 1);
            f.dateTo = buildISO(year, 11, 31);
            consumed[i] = true;
            matched.push({ span: { start: i, end: i + 1 }, dimension: 'dateRange' });
            return;
        }

        // "this / last / next year".
        if (tokens[i] === 'year' && i > 0) {
            const rel = tokens[i - 1];
            const year = now.getFullYear() + (rel === 'last' || rel === 'previous' ? -1 : rel === 'next' ? 1 : (rel === 'this' ? 0 : null));
            if (year !== null && !Number.isNaN(year) && ['this', 'last', 'previous', 'next'].includes(rel)) {
                f.dateFrom = buildISO(year, 0, 1);
                f.dateTo = buildISO(year, 11, 31);
                consumeRange(consumed, i - 1, i + 1);
                matched.push({ span: { start: i - 1, end: i + 1 }, dimension: 'dateRange' });
                return;
            }
        }

        // "next / last week" — the adjacent ISO week.
        if (tokens[i] === 'week' && i > 0 && (tokens[i - 1] === 'next' || tokens[i - 1] === 'last' || tokens[i - 1] === 'previous')) {
            const offset = tokens[i - 1] === 'next' ? 7 : -7;
            const monday = startOfISOWeek(addDays(now, offset));
            f.dateFrom = toISO(monday);
            f.dateTo = toISO(addDays(monday, 6));
            consumeRange(consumed, i - 1, i + 1);
            matched.push({ span: { start: i - 1, end: i + 1 }, dimension: 'dateRange' });
            return;
        }
    }
}

/** Monday of the ISO week containing `date`. */
function startOfISOWeek(date) {
    const day = date.getDay(); // 0 = Sunday
    const diff = (day + 6) % 7; // days since Monday
    return addDays(date, -diff);
}

function applyDateRanges(tokens, consumed, query, now, matched) {
    const f = query.filters;
    // "start"/"starting" anywhere means the range is about startDate, not endDate.
    if (tokens.some(token => token === 'starting' || token === 'started' || token === 'start')) {
        f.dateField = 'startDate';
    }

    for (let i = 0; i < tokens.length; i++) {
        if (consumed[i]) continue;

        // "between X and Y"
        if (tokens[i] === 'between' || tokens[i] === 'entre') {
            const first = parseDateAt(tokens, i + 1, now);
            if (!first) continue;
            let j = first.end;
            if (tokens[j] === 'and' || tokens[j] === 'y') j++;
            const second = parseDateAt(tokens, j, now);
            if (!second) continue;
            f.dateFrom = first.iso;
            f.dateTo = second.iso;
            consumeRange(consumed, i, second.end);
            matched.push({ span: { start: i, end: second.end }, dimension: 'dateRange' });
            continue;
        }

        const bound = RANGE_MARKERS[tokens[i]];
        if (!bound) continue;
        const date = parseDateAt(tokens, i + 1, now);
        if (!date) continue;
        // "before Monday" → dateTo; "after Friday" → dateFrom. Both together is a
        // contradiction we deliberately preserve — the validator refuses it rather
        // than silently picking one.
        if (bound === 'to') f.dateTo = date.iso; else f.dateFrom = date.iso;
        consumeRange(consumed, i, date.end);
        matched.push({ span: { start: i, end: date.end }, dimension: 'dateRange' });
    }
}

/** Claim exact deadlines before the generic date aliases and live names. */
function applyExactDeadline(tokens, consumed, query, now, matched, unresolved) {
    if (query.entity !== 'tasks' || query.filters.dateFrom || query.filters.dateTo) return;
    const cues = Object.entries(DATE_FIELD_CUES).flatMap(([field, phrases]) => phrases.map(phrase => ({ field, phrase })))
        .sort((a, b) => b.phrase.length - a.phrase.length);
    for (let i = 0; i < tokens.length; i++) {
        if (consumed[i]) continue;
        const remainder = tokens.slice(i).join(' ');
        const hit = cues.find(cue => remainder === cue.phrase || remainder.startsWith(`${cue.phrase} `));
        let start = i;
        if (hit) start += hit.phrase.split(' ').length;
        if (['on', 'of', 'el', 'del', 'para', 'en', 'al'].includes(tokens[start])) start++;
        if (tokens[start] === 'dia') start++;
        const date = parseDateAt(tokens, start, now);
        if (!date) {
            const rest = tokens.slice(start).join(' ');
            const namedMonth = MONTHS.includes(tokens[start]) || MONTHS_ES.includes(tokens[start]);
            const nextMonth = tokens[start + (tokens[start + 1] === 'de' ? 2 : 1)];
            const looksInvalid = /^\d{4}-\d{2}-\d{2}$|^\d{1,2}-\d{1,2}(?:-\d{4})?$/.test(tokens[start] || '')
                || (/^\d{1,2}$/.test(tokens[start] || '') && (MONTHS.includes(nextMonth) || MONTHS_ES.includes(nextMonth)))
                || (namedMonth && /^\d{1,2}$/.test(tokens[start + 1] || ''));
            if (looksInvalid) {
                unresolved.push({ term: rest, kind: 'ambiguous', candidates: [] });
                consumeRange(consumed, i, tokens.length);
                return;
            }
            if (hit && (namedMonth || Object.keys(DATE_PRESET_ALIASES).some(alias => rest === alias || rest.startsWith(`${alias} `)))) {
                query.filters.dateField = hit.field;
                consumeRange(consumed, i, start);
            }
            continue;
        }
        if (consumed.slice(i, date.end).some(Boolean)) continue;
        // Bare today/tomorrow keep their established presets. A named month
        // without a day remains a month range (or a live tag such as Julio).
        const relative = ['today', 'hoy', 'tomorrow', 'manana', 'yesterday', 'ayer'].includes(tokens[start]);
        const explicitStart = date.end + (tokens[date.end] === 'el' || tokens[date.end] === 'on' ? 1 : 0);
        const explicit = relative ? parseDateAt(tokens, explicitStart, now) : null;
        if (relative && !explicit && hit?.field !== 'startDate' && ['today', 'hoy', 'tomorrow', 'manana'].includes(tokens[start])) {
            if (hit) consumeRange(consumed, i, start);
            continue;
        }
        if (!hit && relative && !explicit) continue;
        const resolved = explicit || date;
        if (explicit && explicit.iso !== date.iso) {
            unresolved.push({ term: tokens.slice(i, resolved.end).join(' '), kind: 'ambiguous', candidates: [date.iso, explicit.iso] });
            consumeRange(consumed, i, resolved.end);
            return;
        }
        query.filters.dateFrom = resolved.iso;
        query.filters.dateTo = resolved.iso;
        if (hit) query.filters.dateField = hit.field;
        consumeRange(consumed, i, resolved.end);
        matched.push({ span: { start: i, end: resolved.end }, dimension: 'dateRange' });
        return;
    }
}

/** Parse a date starting at tokens[i]. Returns { iso, end } or null. */
function parseDateAt(tokens, i, now) {
    if (i >= tokens.length) return null;
    const token = tokens[i];
    if (!token) return null;

    if (/^\d{4}-\d{2}-\d{2}$/.test(token)) {
        const [year, month, day] = token.split('-').map(Number);
        if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month - 1)) return null;
        return { iso: token, end: i + 1 };
    }
    const numeric = token.match(/^(\d{1,2})-(\d{1,2})(?:-(\d{4}))?$/);
    if (numeric) {
        const day = Number(numeric[1]);
        const month = Number(numeric[2]) - 1;
        const year = numeric[3] ? Number(numeric[3]) : now.getFullYear();
        if (month >= 0 && month < 12 && day >= 1 && day <= daysInMonth(year, month)) {
            return { iso: buildISO(year, month, day), end: i + 1 };
        }
        return null;
    }
    if (token === 'today' || token === 'hoy') return { iso: toISO(now), end: i + 1 };
    if (token === 'tomorrow' || token === 'manana') return { iso: toISO(addDays(now, 1)), end: i + 1 };
    if (token === 'yesterday' || token === 'ayer') return { iso: toISO(addDays(now, -1)), end: i + 1 };

    const weekday = WEEKDAYS.indexOf(token) >= 0 ? WEEKDAYS.indexOf(token) : WEEKDAYS_ES.indexOf(token);
    if (weekday >= 0) return { iso: toISO(nextWeekday(now, weekday)), end: i + 1 };

    // "july 20" / "july 20 2026" / "20 july"
    let month = MONTHS.indexOf(token) >= 0 ? MONTHS.indexOf(token) : MONTHS_ES.indexOf(token);
    if (month >= 0) {
        const day = /^\d{1,2}$/.test(tokens[i + 1] || '') ? Number(tokens[i + 1]) : NaN;
        if (!Number.isFinite(day) || day < 1) return null;
        const year = parseInt(tokens[i + 2], 10);
        const hasYear = Number.isFinite(year) && year > 1900;
        if (day > daysInMonth(hasYear ? year : now.getFullYear(), month)) return null;
        return { iso: buildISO(hasYear ? year : now.getFullYear(), month, day), end: i + (hasYear ? 3 : 2) };
    }
    const day = /^\d{1,2}$/.test(token) ? Number(token) : NaN;
    if (Number.isFinite(day) && day >= 1 && day <= 31) {
        const monthStart = i + (tokens[i + 1] === 'de' ? 2 : 1);
        const next = tokens[monthStart];
        const nextMonth = MONTHS.indexOf(next) >= 0 ? MONTHS.indexOf(next) : MONTHS_ES.indexOf(next);
        if (nextMonth >= 0) {
            const yearStart = monthStart + (tokens[monthStart + 1] === 'de' || tokens[monthStart + 1] === 'del' ? 2 : 1);
            const hasYear = /^\d{4}$/.test(tokens[yearStart] || '');
            const year = hasYear ? Number(tokens[yearStart]) : now.getFullYear();
            if (day > daysInMonth(year, nextMonth)) return null;
            return { iso: buildISO(year, nextMonth, day), end: hasYear ? yearStart + 1 : monthStart + 1 };
        }
    }
    return null;
}

function toISO(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}
function buildISO(year, monthIndex, day) {
    return `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
function addDays(date, days) {
    const next = new Date(date.getTime());
    next.setDate(next.getDate() + days);
    return next;
}
/** The next occurrence of a weekday, always in the future. */
function nextWeekday(now, weekday) {
    const delta = (weekday - now.getDay() + 7) % 7 || 7;
    return addDays(now, delta);
}

// ---------------------------------------------------------------------------
// Free text + leftovers
// ---------------------------------------------------------------------------

function applySearchPhrase(tokens, consumed, query, matched) {
    for (const preposition of SEARCH_PREPOSITIONS) {
        const key = normalizeText(preposition).split(' ');
        const start = findSpan(tokens, consumed, key.join(' '));
        if (!start) continue;

        const words = [];
        let i = start.end;
        while (i < tokens.length && !consumed[i]) {
            const token = tokens[i];
            if (token === 'and' || token === 'or' || token === 'in' || token === 'from') break;
            if (!STOPWORDS.has(token) || words.length) words.push(token);
            i++;
        }
        if (!words.length) continue;
        query.filters.search = words.join(' ');
        consumeRange(consumed, start.start, i);
        matched.push({ span: { start: start.start, end: i }, dimension: 'search' });
        return;
    }
}

/**
 * A leftover word right after "in"/"from"/"assigned to" is meant to be a project
 * or a tag. If we can't resolve it against real data, say so — never invent it.
 */
function resolveLeftovers(tokens, consumed, query, vocabulary, entity, unresolved, matched) {
    const tagNames = (vocabulary.tags || []).map(tag => (typeof tag === 'string' ? tag : tag?.name)).filter(Boolean);
    const projectNames = (vocabulary.projects || []).map(project => project?.name).filter(Boolean);

    for (let i = 0; i < tokens.length; i++) {
        if (consumed[i] || STOPWORDS.has(tokens[i])) continue;
        // Too short to fuzzy-match safely: at this length almost any word is within
        // edit distance of almost any other, so a match means nothing.
        if (tokens[i].length < 3) continue;
        const context = precedingPhrase(tokens, i);
        const wantsTag = TAG_PREPOSITIONS.some(p => context.endsWith(normalizeText(p)));
        const wantsProject = PROJECT_PREPOSITIONS.some(p => context.endsWith(normalizeText(p)));
        if (!wantsTag && !wantsProject) continue;

        const term = tokens[i];
        const pool = wantsProject && !wantsTag ? projectNames : [...tagNames, ...projectNames];
        const candidates = nearestNames(term, pool, 3);

        // A single close match is a typo we can fix. Two are an ambiguity the user
        // must settle — picking one arbitrarily is exactly what we must not do.
        const exact = candidates.filter(name => levenshtein(normalizeText(name), term) <= 1);
        if (exact.length === 1) {
            const resolved = exact[0];
            const project = (vocabulary.projects || []).find(p => normalizeText(p.name) === normalizeText(resolved));
            if (project && wantsProject) query.filters.projects.push(String(project.id));
            else query.filters.tags.push(resolved);
            consumed[i] = true;
            matched.push({ span: { start: i, end: i + 1 }, dimension: project ? 'project' : 'tag', fuzzy: true });
            continue;
        }

        unresolved.push({
            term,
            kind: candidates.length > 1 ? 'ambiguous' : 'unknown',
            candidates
        });
        consumed[i] = true;
    }
}

const PROJECT_FRAGMENT_CUES = [
    ...TAG_PREPOSITIONS, ...PROJECT_PREPOSITIONS, ...PROJECT_CUES, ...PROJECT_NAME_PREFIX_WORDS
].map(normalizeText);
const MAX_PROJECT_FRAGMENT = 6;

/**
 * A run of words after a cue that is a complete-word part of a project name:
 * "de campaña 3" → "Obj. 1: CAMPAÑA 3". The longest run wins; a run shared by
 * several projects ("de campaña") is asked about, never picked.
 */
function resolveProjectFragments(tokens, consumed, query, vocabulary, entity, unresolved, matched) {
    if (entity === 'projects') return;
    const projects = (vocabulary.projects || [])
        .filter(project => project?.name && project.id != null)
        .map(project => ({ project, aliases: projectNameAliases(project.name).map(normalizeText) }));
    if (!projects.length) return;

    for (let i = 0; i < tokens.length; i++) {
        if (consumed[i] || STOPWORDS.has(tokens[i])) continue;
        // "objetivo" is a cue for the next word, unless it opens "objetivo 2".
        if (PROJECT_NAME_PREFIX_WORDS.includes(tokens[i]) && !/^\d+$/.test(tokens[i + 1] || '')) continue;
        const context = precedingPhrase(tokens, i);
        if (!PROJECT_FRAGMENT_CUES.some(cue => context === cue || context.endsWith(` ${cue}`))) continue;

        let runEnd = i;
        while (runEnd < tokens.length && !consumed[runEnd] && runEnd - i < MAX_PROJECT_FRAGMENT) runEnd++;
        let resolved = false;
        for (let end = runEnd; end > i && !resolved; end--) {
            const words = tokens.slice(i, end);
            if (STOPWORDS.has(words[words.length - 1])) continue;
            // A bare number ("3"), a two-letter word or "objetivo" alone names nothing;
            // "objetivo 2" does.
            const isPrefix = word => PROJECT_NAME_PREFIX_WORDS.includes(word);
            const names = words.some(word => word.length >= 3 && !/^\d+$/.test(word) && !isPrefix(word))
                || (isPrefix(words[0]) && /^\d+$/.test(words[1] || ''));
            if (!names) continue;
            const phrase = words.join(' ');
            const hits = projects.filter(({ aliases }) => aliases.some(alias => ` ${alias} `.includes(` ${phrase} `)));
            if (!hits.length) continue;

            const exact = hits.filter(({ aliases }) => aliases.includes(phrase));
            const chosen = exact.length === 1 ? exact : hits;
            const span = { start: i, end };
            if (chosen.length === 1) {
                const negated = hasNegationBefore(tokens, consumed, i);
                applyMeaning(query, { dimension: 'project', value: String(chosen[0].project.id) }, { negated, tokens, span });
                if (negated) consumed[negated.index] = true;
                matched.push({ span, phrase, dimension: 'project', partial: true });
            } else {
                const candidates = chosen.map(({ project }) => project.name).sort();
                unresolved.push({ term: phrase, kind: 'ambiguous', candidates });
                matched.push({ span, phrase, dimension: 'unresolved' });
            }
            consume(consumed, span);
            // "del objetivo campaña 3": the structural cue is explained by the match.
            for (let k = Math.max(0, i - 2); k < i; k++) {
                if (PROJECT_NAME_PREFIX_WORDS.includes(tokens[k])) consumed[k] = true;
            }
            i = end - 1;
            resolved = true;
        }
        // "objetivo 9" plainly names a project, just not one that exists — say so.
        if (!resolved && PROJECT_NAME_PREFIX_WORDS.includes(tokens[i]) && /^\d+$/.test(tokens[i + 1] || '')) {
            const span = { start: i, end: i + 2 };
            unresolved.push({ term: tokens.slice(i, i + 2).join(' '), kind: 'unknown', candidates: [] });
            matched.push({ span, dimension: 'unresolved' });
            consume(consumed, span);
            i += 1;
        }
    }
}

function precedingPhrase(tokens, i) {
    return tokens.slice(Math.max(0, i - 3), i).join(' ');
}

// ---------------------------------------------------------------------------
// Scoring
// ---------------------------------------------------------------------------

function scoreConfidence({ tokens, consumed, unresolved, matched, leftovers }) {
    const contentTokens = tokens.filter(token => !STOPWORDS.has(token));
    if (!contentTokens.length) return matched.length ? 1 : 0;

    const matchedContent = tokens.filter((token, i) => consumed[i] && !STOPWORDS.has(token)).length;
    const coverage = matchedContent / contentTokens.length;

    const fuzzy = matched.filter(m => m.fuzzy).length;
    const ambiguous = unresolved.filter(u => u.kind === 'ambiguous').length;
    const certainty = Math.max(0, 1 - (0.15 * fuzzy) - (0.5 * ambiguous) - (0.1 * leftovers.length));

    return Math.max(0, Math.min(1, (coverage * 0.6) + (certainty * 0.4)));
}

function shouldEscalate({ query, confidence, unresolved, leftovers, normalized, matched, unsupported }) {
    // An unsupported dimension is an answer, not a failure — never spend an API
    // call to be told again that Nautilus has no sprints.
    const onlyUnsupported = unsupported.length > 0 && matched.every(m => m.dimension === 'unsupported');
    if (onlyUnsupported) return false;

    // A name we could not settle is never rescued by escalating. The LLM is handed
    // the same vocabulary we just searched, so it can only guess — and an ambiguity
    // is for the user to resolve, not the model. Answer from what we have.
    if (unresolved.length) return false;

    // If the scanner produced a usable filter and nothing meaningful is dangling,
    // TRUST IT. Escalating a query we already read correctly only invites the model
    // to "improve" it — in practice, to hallucinate an extra filter (a stray
    // "no project", the whole tag list) that turns a good result into an empty one.
    // This is the single biggest source of wrong answers, so the bar to escalate is:
    // we resolved little, and there is clearly more sentence we failed to read.
    const resolved = countKaiQueryFilters(query) + (query.scope === 'archived' ? 1 : 0);
    if (resolved > 0 && leftovers.length <= 2) return false;

    // Multi-clause phrasing is where the scanner's flat span model gives up.
    if (/\bbut\b|\band also\b|\bexcept when\b|\bunless\b/.test(normalized)) return true;

    // Nothing (or almost nothing) resolved, and there is real content we couldn't
    // read — let the model try.
    if (resolved === 0 && leftovers.length >= 2) return true;
    if (confidence < KAI_CONFIDENCE_THRESHOLD && resolved === 0) return true;
    return false;
}

// ---------------------------------------------------------------------------
// Span helpers
// ---------------------------------------------------------------------------

function findSpan(tokens, consumed, phrase) {
    const words = phrase.split(' ').filter(Boolean);
    if (!words.length) return null;
    for (let i = 0; i + words.length <= tokens.length; i++) {
        const span = { start: i, end: i + words.length };
        if (spanIsConsumed(consumed, span)) continue;
        if (words.every((word, k) => tokens[i + k] === word)) return span;
    }
    return null;
}

function spanIsConsumed(consumed, span) {
    for (let i = span.start; i < span.end; i++) if (consumed[i]) return true;
    return false;
}

function consume(consumed, span) {
    for (let i = span.start; i < span.end; i++) consumed[i] = true;
}

function consumeRange(consumed, start, end) {
    for (let i = start; i < end; i++) consumed[i] = true;
}

/** Mark every token appearing in a regex hit as consumed (spans may be ragged). */
function consumeMatchedWords(tokens, consumed, phrase) {
    const words = new Set(normalizeText(phrase).split(' ').filter(Boolean));
    for (let i = 0; i < tokens.length; i++) {
        if (words.has(tokens[i])) consumed[i] = true;
    }
}
